import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface IPostOnboardingLaunchSeed {
    /** Completed survey progress (for example `6/6`) retained across the card swap. */
    progressLabel: string
}

interface IPostOnboardingPromptStore {
    /**
     * True from the moment the first brand is created until onboarding finishes.
     *
     * PERSISTED. There is no server marker for the window between "brand created" and "questionnaire
     * submitted", and the two client signals that used to cover it both go false inside it —
     * `useIsUserInOnboarding()` flips the instant the brand becomes real, and this flag used to be
     * in-memory. A reload in that window therefore released the sidebar lock. Cleared by
     * `completeOnboardingFlow`.
     */
    isOnboardingFlowActive: boolean

    /**
     * Chat that just submitted the questionnaire. Session-only, and only an optimisation: it hands the
     * bottom slot over in the same frame. Whether the launch still needs to run is read from the
     * transcript instead (see `useOnboardingHandoffPending`), which is what makes it survive a reload.
     */
    nextTaskStepAgentUuid: string | null
    nextTaskStepSeed: IPostOnboardingLaunchSeed | null

    /**
     * Epoch ms of the settled chat's hand-off to the dashboard, or null. PERSISTED: it is what lets
     * MobileBlocker cover the dashboard IMMEDIATELY on arrival — before `onboardingCompleted` has
     * round-tripped — while the launch keeps running hidden underneath. Set by the platform-agnostic
     * hand-off; only the blocker gives it mobile-specific meaning.
     *
     * Deliberately NOT cleared by `completeOnboardingFlow`: that fires when the launch POST
     * resolves, which `onboardingCompleted` lags (read-replica) — clearing there reopened the very
     * dashboard flash the flag exists to prevent. It is cleared when the blocker OBSERVES the
     * completed flip, by a fresh `startOnboardingFlow`, and — because localStorage outlives
     * accounts/orgs on a shared device — it expires via `LAUNCH_HANDOFF_TTL_MS`, so a stale flag
     * can never blanket a future user's onboarding chat.
     */
    launchHandoffStartedAt: number | null

    /** Begin the first-brand onboarding flow. */
    startOnboardingFlow: () => void
    /** Hand the bottom slot to the launch the moment the questionnaire is submitted. */
    showNextTaskStep: (agentUuid: string, seed: IPostOnboardingLaunchSeed) => void
    /** Mark the dashboard hand-off as in flight (set right before the redirect). */
    beginLaunchHandoff: () => void
    /** Drop the hand-off marker (the blocker calls this once `onboardingCompleted` is observed). */
    clearLaunchHandoff: () => void
    /** Release the flow lock and clear the session latch after the launch finishes. */
    completeOnboardingFlow: () => void
}

/**
 * How long a persisted hand-off marker stays meaningful. Comfortably above the whole hand-off
 * (5-min stream ceiling + 2-min completion poll); anything older is a leftover from an abandoned
 * or foreign flow and must not activate the blocker's cover.
 */
export const LAUNCH_HANDOFF_TTL_MS = 15 * 60_000

/** Whether a persisted hand-off marker is still live (fresh enough to mean "launch in flight"). */
export function isLaunchHandoffLive(startedAt: number | null): boolean {
    return startedAt !== null && Date.now() - startedAt < LAUNCH_HANDOFF_TTL_MS
}

export const POST_ONBOARDING_PROMPT_STORAGE_KEY = 'post-onboarding-flow'

export const usePostOnboardingPromptStore = create<IPostOnboardingPromptStore>()(
    persist(
        (set) => ({
            isOnboardingFlowActive: false,
            nextTaskStepAgentUuid: null,
            nextTaskStepSeed: null,
            launchHandoffStartedAt: null,

            startOnboardingFlow: () =>
                set((s) =>
                    // A latch that is already up means the questionnaire was submitted BEFORE this ran:
                    // `storeBrandInformation`'s post-paint effect arms the flow only once the brand
                    // query resolves, which can land after the user answered the survey. Clearing it
                    // would withdraw the handoff and fall the bottom slot back to the survey ask,
                    // remounting it at question 1.
                    s.nextTaskStepAgentUuid !== null
                        ? { isOnboardingFlowActive: true }
                        : {
                              isOnboardingFlowActive: true,
                              nextTaskStepAgentUuid: null,
                              nextTaskStepSeed: null,
                              launchHandoffStartedAt: null,
                          }
                ),
            showNextTaskStep: (agentUuid, seed) => set({ nextTaskStepAgentUuid: agentUuid, nextTaskStepSeed: seed }),
            beginLaunchHandoff: () => set({ launchHandoffStartedAt: Date.now() }),
            clearLaunchHandoff: () => set({ launchHandoffStartedAt: null }),
            completeOnboardingFlow: () =>
                set({
                    isOnboardingFlowActive: false,
                    nextTaskStepAgentUuid: null,
                    nextTaskStepSeed: null,
                }),
        }),
        {
            name: POST_ONBOARDING_PROMPT_STORAGE_KEY,
            // Only the lock survives a reload. The latch is scoped to a live chat, and the launch no
            // longer needs remembering — the transcript records whether it has run.
            partialize: (state) => ({
                isOnboardingFlowActive: state.isOnboardingFlowActive,
                launchHandoffStartedAt: state.launchHandoffStartedAt,
            }),
        }
    )
)
